/* @jsx h */
import { defineComponent, ref, onMounted, h, computed } from "vue";
import { useInfiniteQuery, useQueryClient } from "@tanstack/vue-query";
import { Payment, RawPayment } from "@lemmonhq/types";
import { setTitle } from "@lemmonhq/orders";
import {
  getDateRange,
  syncedRef,
  useLocalization,
  PageHeader,
} from "@lemmonhq/uikit";
import {
  useAuth,
  useCheckout,
  useToast,
  useTrpc,
} from "@app/vue/providers/index";
import { sharedDateRangeKey } from "@app/vue/composables/sharedDateRangeKey";
import {
  CartItem,
  CartPersona,
  CartSelectedBundle,
  CheckoutData,
} from "@lemmonhq/types";
import { OpenCheckoutsSection } from "./components/OpenCheckoutsSection";
import { PrintReceiptModal } from "./PrintReceiptModal";
import {
  TransactionFilters,
  TxFilters,
  buildFilterSummary,
} from "./components/TransactionFilters";
import { TransactionList } from "./components/TransactionList";
import { EditPaymentModal } from "./components/EditPaymentModal";
import { VoidPaymentModal } from "./components/VoidPaymentModal";
import { CreditNoteModal } from "./components/credit-note/CreditNoteModal";
import type { CreditNoteSubmit } from "./components/credit-note/types";
import { useCreditNoteSubmit } from "./components/credit-note/useCreditNoteSubmit";
import { api } from "@app/data/index";
import { PaymentShow } from "./PaymentShow";
import { DigitalReceiptModal } from "@app/vue/components/CheckoutComponent/DigitalReceiptModal";
import { useShop } from "@app/vue/providers/ShopConnection";
import { normalizeTransactionHistoryDateRange } from "./dateRangePolicy";

export const TransactionHistoryPage = defineComponent({
  name: "TransactionHistoryPage",
  setup() {
    const { showToast } = useToast();
    const trpc = useTrpc();
    const auth = useAuth();
    const checkout = useCheckout();
    const queryClient = useQueryClient();
    const { t } = useLocalization();
    const { refreshData } = useShop();
    setTitle("Payments");

    // ── Modal state ───────────────────────────────────────────────────────────
    const selectedPaymentId = ref<number | null>(null);
    const showEditPaymentModal = ref<RawPayment>();
    // "Add refund" and "Reopen table" both open the credit note dialog.
    const showVoidPaymentModal = ref<RawPayment>();
    const showCreditNoteModal = ref<{ payment: RawPayment; reopen: boolean }>();
    const showDigitalReceipt = ref<RawPayment>();
    const selectedPayment = ref<RawPayment>();

    // ── Filter state ──────────────────────────────────────────────────────────
    // The range is shared with the report pages; everything else here is local
    // to this page. `filters` stays a single object so the query key, the
    // summary and the filter sheet keep reading one value.
    const isStaff = auth.user.value?.role === "staff";
    const dateRange = syncedRef(
      sharedDateRangeKey(
        auth.user.value?.restaurant_id,
        auth.user.value?.id,
      ),
      {
        type: "today",
        ...getDateRange("today"),
      },
    );
    // A staff member may have an out-of-policy value saved by another report
    // page. Repair it immediately so the query never runs for that range.
    dateRange.value = normalizeTransactionHistoryDateRange(
      dateRange.value,
      isStaff,
    );
    const otherFilters = ref<Omit<TxFilters, "dateRange">>({});

    const filters = computed<TxFilters>({
      get: () => ({ ...otherFilters.value, dateRange: dateRange.value }),
      set: (next) => {
        const { dateRange: nextRange, ...rest } = next;
        dateRange.value = normalizeTransactionHistoryDateRange(
          nextRange,
          isStaff,
        );
        otherFilters.value = rest;
      },
    });

    const selectedStaffName = computed(() => {
      if (!filters.value.userId) return undefined;
      const users = [
        ...(auth.user.value ? [auth.user.value] : []),
        ...(auth.user.value?.colleagues ?? []),
      ];
      return users.find(
        (user) =>
          user.id === filters.value.userId &&
          user.role === "staff" &&
          !user.profile.deleted_at,
      )?.profile.name;
    });

    const filterSummary = computed(() =>
      buildFilterSummary(filters.value, t, selectedStaffName.value),
    );

    const activeFilterCount = computed(() => {
      let n = 0;
      if (filters.value.dateRange.type !== "today") n++;
      if (filters.value.method) n++;
      if (filters.value.type) n++;
      if (filters.value.status) n++;
      if (filters.value.userId) n++;
      return n;
    });

    // ── Infinite query via tRPC ───────────────────────────────────────────────
    const paymentsQuery = useInfiniteQuery({
      queryKey: computed(() => ["payments", "list", filters.value]),
      queryFn: async ({ pageParam }) => {
        const cursor = pageParam as
          | { id: number; created_at: string }
          | undefined;
        return trpc.payments.listPayments.query({
          limit: 25,
          cursor: cursor ?? undefined,
          direction: "after",
          method: filters.value.method,
          type: filters.value.type,
          status: filters.value.status,
          user_id: filters.value.userId,
          start_datetime: `${filters.value.dateRange.startDate}T00:00:00`,
          end_datetime: `${filters.value.dateRange.endDate}T23:59:59`,
        });
      },
      initialPageParam: undefined as
        | { id: number; created_at: string }
        | undefined,
      getNextPageParam: (lastPage: any) => {
        if (!lastPage.pagination.has_next_page) return undefined;
        return lastPage.pagination.next_cursor;
      },
    });

    const allPayments = computed<Payment[]>(
      () =>
        paymentsQuery.data.value?.pages.flatMap(
          (p: any) => (p.data as Payment[]) ?? [],
        ) ?? [],
    );

    // ── Infinite scroll ───────────────────────────────────────────────────────
    const scrollContainerRef = ref<HTMLElement | null>(null);

    const onScroll = () => {
      const el = scrollContainerRef.value;
      if (!el) return;
      if (
        el.scrollHeight - el.scrollTop - el.clientHeight < 300 &&
        paymentsQuery.hasNextPage.value &&
        !paymentsQuery.isFetchingNextPage.value
      ) {
        paymentsQuery.fetchNextPage();
      }
    };

    onMounted(() => setTitle(t("reports.tx.page.title")));

    // ── Action handlers ───────────────────────────────────────────────────────
    const handleEditPayment = async (
      id: string,
      data: { payment_method: string },
    ) => {
      try {
        await api.updatePayment(id, data);
        showToast(t("general.success_notification.payment_edited"), {
          type: "success",
        });
        showEditPaymentModal.value = undefined;
        queryClient.invalidateQueries({ queryKey: ["payments", "list"] });
      } catch (error) {
        console.error("Failed to edit payment", error);
        showToast(t("reports.tx.page.edit_failed"), { type: "error" });
      }
    };

    const handleVoidPayment = async (paymentId: number) => {
      const isCredit = showVoidPaymentModal.value?.type === "credit";
      try {
        await trpc.payments.voidPayment.mutate({ payment_id: paymentId });
        showToast(
          isCredit
            ? t("reports.tx.page.void_credit_success")
            : t("reports.tx.page.void_success"),
          { type: "success" },
        );
        showVoidPaymentModal.value = undefined;
        queryClient.invalidateQueries({ queryKey: ["payments", "list"] });
      } catch (error) {
        console.error("Failed to void payment", error);
        showToast(
          isCredit
            ? t("reports.tx.page.void_credit_failed")
            : t("reports.tx.page.void_failed"),
          { type: "error" },
        );
      }
    };

    const submitCreditNote = useCreditNoteSubmit();
    const handleCreditNote = async (input: CreditNoteSubmit) => {
      const target = showCreditNoteModal.value;
      if (!target) return;
      const result = await submitCreditNote(target.payment, input);
      if (!result) return;
      showCreditNoteModal.value = undefined;
      queryClient.invalidateQueries({ queryKey: ["payments", "list"] });
      if (result.reopened) {
        try {
          await refreshData();
        } catch (refreshError) {
          console.error("Failed to refresh shop data", refreshError);
        }
      }
    };

    // Rehydrate an open staff_checkout and reopen the checkout so the cashier
    // can collect the remaining legs or finalize. The stored cart (create_order
    // format) is canonical for finalization; here we reconstruct just enough of
    // the frontend Cart (cartId/price/quantity, best-effort name) to drive the
    // split/partial editor. See docs/plans/checkout_editor/09_transaction_history_resume.md.
    const handleResume = async (id: string) => {
      try {
        const { checkout: row, payments } = (await trpc.staffCheckouts.get.query(
          { id },
        )) as any;

        const products = await api.getProducts().catch(() => []);
        const nameById = new Map<number, string>(
          (products as any[]).map((p) => [p.id, p.name]),
        );

        // The stored cart is create_order format, where bundle options are a
        // flat array (one entry per option). Regroup them back into
        // selectedBundles so calculateCartItemTotal adds the bundle price —
        // omitting them understates each line and, since the DB does not
        // validate coverage, lets a bundled checkout finalize underpaid.
        const rebuildSelectedBundles = (
          bundle: any[] | undefined,
        ): CartSelectedBundle[] | undefined => {
          if (!Array.isArray(bundle) || bundle.length === 0) return undefined;
          const byBundle = new Map<number, CartSelectedBundle>();
          for (const opt of bundle) {
            let group = byBundle.get(opt.bundle_id);
            if (!group) {
              group = {
                bundleId: opt.bundle_id,
                name: opt.bundle_name,
                options: [],
              };
              byBundle.set(opt.bundle_id, group);
            }
            group.options.push({
              id: opt.option_id,
              entity_id: opt.entity_id,
              entity_type: opt.entity_type,
              title: opt.option_name,
              price: opt.price,
            });
          }
          return Array.from(byBundle.values());
        };

        const backendItems: any[] = Array.isArray(row.cart) ? row.cart : [];
        const items: CartItem[] = backendItems.map((bi) => ({
          guid: crypto.randomUUID(),
          cartId: bi.cart_id,
          productId: bi.product_id,
          name: nameById.get(bi.product_id) || `#${bi.product_id}`,
          price: String(bi.price),
          fullPrice: Number(bi.price),
          maxRetailPrice: Number(bi.price),
          quantity: Number(bi.quantity),
          type: bi.type || "restaurant",
          foodTypeId: bi.food_type_id ?? undefined,
          // Kitchen note — serialized as `notes` by getCreateOrderPayload.
          note: bi.notes ?? undefined,
          selectedBundles: rebuildSelectedBundles(bi.bundle),
          isCustom: false,
        })) as unknown as CartItem[];

        const staffCheckoutPayments = (payments as any[]).map((p) => ({
          paymentId: Number(p.id),
          paymentGuid: p.guid,
          amount: Number(p.amount),
          // Gross coverage — the remaining balance is counted on this.
          subtotal: Number(p.subtotal ?? p.amount),
          method: p.method,
          items: Array.isArray(p.partial_items)
            ? p.partial_items.map((it: any) => ({
                cartId: it.cartId,
                quantity: Number(it.quantity),
              }))
            : undefined,
        }));

        // Order-creation fields the serialized cart items don't carry. finalize
        // replays these into create_order from the DB, so the resumed CLIENT
        // cart has to carry them too or the two views of the checkout diverge.
        //
        // The delivery fee is the load-bearing one: the server's coverage target
        // is `subtotal + deliveryFee`, and the split gate in CheckoutComponent
        // reads the fee off THIS cart. Dropping it here made a delivery checkout
        // look splittable, so legs covering only the subtotal could be collected
        // against a total that finalize would then reject as underpaid —
        // captured money on a checkout that can never complete.
        const metadata = (row.metadata ?? {}) as {
          deliveryFee?: number | null;
          deliveryFeeLabel?: string | null;
          isMultiCourse?: boolean;
          triggeredFoodTypeIds?: number[];
          reservationId?: string | null;
          personas?: unknown;
        };
        const deliveryFeeValue = Number(metadata.deliveryFee ?? 0);

        const rehydrated: CheckoutData = {
          payload: {
            type: "cart",
            object: {
              items,
              customers: (metadata.personas ?? []) as CartPersona[],
              checkoutIds: [],
              tableId: row.table_id ?? null,
              reservationId: metadata.reservationId ?? null,
              isMultiCourse: metadata.isMultiCourse ?? false,
              triggeredFoodTypeIds: metadata.triggeredFoodTypeIds ?? [],
              ...(deliveryFeeValue > 0
                ? {
                    deliveryFee: {
                      label: metadata.deliveryFeeLabel ?? "",
                      value: deliveryFeeValue,
                    },
                  }
                : {}),
            },
          },
          serviceMethod: row.service_method,
          ...(row.tips != null && Number(row.tips) > 0
            ? { tip: { value: Number(row.tips) } }
            : {}),
          staffCheckout: {
            id: row.id,
            mode: row.payment_mode ?? undefined,
            payments: staffCheckoutPayments,
          },
        };

        checkout.setCheckout(rehydrated);
        checkout.show({
          onSuccess: () => {
            checkout.setCheckout(null);
          },
          onCancel: () => {
            checkout.setCheckout(null);
          },
        });
      } catch (e: any) {
        showToast(e?.message || "Failed to resume checkout", { type: "error" });
      }
    };

    return () => (
      <div class="h-full flex flex-col flex-1 overflow-hidden">
        <PageHeader
          title={t("reports.tx.page.title")}
          filterSummary={filterSummary.value}
          activeFilterCount={activeFilterCount.value}
          filterSheetTitle={t("reports.tx.filters.title")}
        >
          {{
            default: ({
              closeMobileSheet,
            }: {
              closeMobileSheet: () => void;
            }) => (
              <TransactionFilters
                filters={filters.value}
                onFiltersChange={(f) => {
                  filters.value = f;
                }}
                onApply={closeMobileSheet}
              />
            ),
          }}
        </PageHeader>

        {/* ── Open (recoverable) staff checkouts ───────────────────────────── */}
        <OpenCheckoutsSection onResume={handleResume} />

        {/* ── Transaction list ─────────────────────────────────────────────── */}
        <div
          ref={scrollContainerRef}
          class="flex-1 overflow-y-auto lg:p-4"
          onScroll={onScroll}
        >
          {paymentsQuery.isError.value ? (
            <div class="flex flex-col items-center justify-center h-64 text-error gap-3">
              <span class="material-symbols-outlined text-5xl opacity-60">
                error_outline
              </span>
              <p class="text-sm font-medium">
                {t("reports.tx.page.error_load")}
              </p>
              <button
                class="btn btn-sm btn-outline btn-error"
                onClick={() => paymentsQuery.refetch()}
              >
                <span class="material-symbols-outlined text-base">refresh</span>
                {t("reports.tx.page.retry_button")}
              </button>
            </div>
          ) : (
            <TransactionList
              transactions={allPayments.value}
              loading={paymentsQuery.isLoading.value}
              loadingMore={paymentsQuery.isFetchingNextPage.value}
              onPrintReceipt={(id) => (selectedPaymentId.value = id)}
              onShowDigitalReceipt={(p) => (showDigitalReceipt.value = p)}
              onShowCreateCreditNode={(id) => {
                const payment = allPayments.value.find((p) => p.id === id);
                if (payment) {
                  showCreditNoteModal.value = { payment, reopen: false };
                }
              }}
              onShowEditPayment={(p) => (showEditPaymentModal.value = p)}
              onShowVoidPayment={(p) => (showVoidPaymentModal.value = p)}
              onShowReopenTable={(p) =>
                (showCreditNoteModal.value = { payment: p, reopen: true })
              }
              onPaymentShow={(p) => (selectedPayment.value = p)}
            />
          )}
        </div>

        {/* ── Modals ───────────────────────────────────────────────────────── */}
        {selectedPaymentId.value && (
          <PrintReceiptModal
            paymentId={selectedPaymentId.value}
            onClose={() => (selectedPaymentId.value = null)}
          />
        )}
        {showEditPaymentModal.value && (
          <EditPaymentModal
            onClose={() => (showEditPaymentModal.value = undefined)}
            onSubmit={handleEditPayment}
            payment={showEditPaymentModal.value}
          />
        )}
        {showVoidPaymentModal.value && (
          <VoidPaymentModal
            onClose={() => (showVoidPaymentModal.value = undefined)}
            onSubmit={handleVoidPayment}
            payment={showVoidPaymentModal.value}
          />
        )}
        {showCreditNoteModal.value && (
          <CreditNoteModal
            key={showCreditNoteModal.value.payment.id}
            payment={showCreditNoteModal.value.payment}
            initialReopen={showCreditNoteModal.value.reopen}
            onClose={() => (showCreditNoteModal.value = undefined)}
            onSubmit={handleCreditNote}
          />
        )}
        {showDigitalReceipt.value ? (
          <DigitalReceiptModal
            paymentId={showDigitalReceipt.value.id}
            paymentGuid={showDigitalReceipt.value.guid}
            total={
              showDigitalReceipt.value.amount +
              (showDigitalReceipt.value.tips || 0)
            }
            onClose={() => (showDigitalReceipt.value = undefined)}
          />
        ) : null}
        {selectedPayment.value ? (
          <PaymentShow
            rawPayment={selectedPayment.value}
            onClose={() => (selectedPayment.value = undefined)}
          />
        ) : null}
      </div>
    );
  },
});
